import * as React from 'react';
import { Alert } from 'omakase';
import { Grid, ScrollListNavigationPanel } from 'ui';
import { core } from '@tock/coretypes';
import {
  menuGroupContainerStyles,
  menuStickyContainerStyles,
} from 'components/business/menu/menuCssDefs';
import { segmentOptionGroupsAndOptions } from '@tock/shared/utils/option';
import { useFeatureFlags } from '@tock/shared/utils/featureFlagContext';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import MenuFilter, {
  getTopLevelOptionGroupsWithFilter,
  getTopLevelOptionsWithFilter,
  getUnionSetOfOptionGroupsToDisplay,
} from 'components/business/menu/MenuFilter';
import MenuGroup from 'components/business/menu/MenuGroup';
import _ from 'lodash';

interface Props {
  /**
   * Is the menu being rendered within TablesideMenuDialog?
   */
  isTableside?: boolean;
  ticketType: core.ITicketType;
  ticketOption: core.ITicketOption[];
  ticketOptionGroup: core.ITicketOptionGroup[];
  optionPrices: core.IPrice[];
  patron?: core.IPatron;
}

export const MENU_CONTAINER_ID = 'experience-menu-dialog-content';

const Menu: React.FunctionComponent<Props> = ({
  isTableside,
  ticketType,
  optionPrices,
  ticketOption,
  ticketOptionGroup,
  patron,
}) => {
  const isFeatureEnabled = useFeatureFlags();
  const [optionGroupsInView, setOptionGroupsInView] = React.useState<{
    [optionGroupId: number]: boolean;
  }>({});
  const [searchText, setSearchText] = React.useState<string>('');

  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.up('sm'));

  const rootMargin = matchesSm ? '-200px 0px 0px 0px' : '-176px 0px 0px 0px';

  const {
    optionsWithModifiers,
    topLevelOptionGroups,
    modifierOptionGroups,
    topLevelTicketOptions,
  } = segmentOptionGroupsAndOptions(ticketOptionGroup, ticketOption, ticketType, {
    isFeatureEnabled,
  });

  const topLevelOptionGroupsWithFilter = getTopLevelOptionGroupsWithFilter(
    searchText,
    topLevelOptionGroups
  );
  const topLevelOptionsWithFilter = getTopLevelOptionsWithFilter(searchText, topLevelTicketOptions);

  const topLevelOptionGroupsToShow = getUnionSetOfOptionGroupsToDisplay(
    topLevelOptionGroups,
    topLevelOptionGroupsWithFilter,
    topLevelOptionsWithFilter
  );

  const offset = 120;
  const offsetIfTableside = 120 + 64;
  const offsetSmIfTableside = 120 + 96;

  return (
    <>
      <div css={menuStickyContainerStyles}>
        <MenuFilter
          value={
            _.filter(
              topLevelOptionGroupsToShow,
              (optionGroup) => !!optionGroup?.id && !!optionGroupsInView[optionGroup.id]
            )?.[0]?.id ?? undefined
          }
          optionGroups={topLevelOptionGroupsToShow}
          onChangeSearch={_.debounce(setSearchText, 200)}
          container={() => document.getElementById(MENU_CONTAINER_ID)} // this should be passed in
          selector={(optionGroupId: number) => `menuOptionGroup-${optionGroupId}`}
          /* Note that these props are misleadingly named. offset is the fallback vertical offset to
             use, offsetSm is the vertical offset to use _above_ the SM breakpoint. */
          /* own height + margin-top for each menu group container. This is the offset that will be used *below* the sm breakpoint size. */
          offset={isTableside ? offsetIfTableside : offset}
          /* own height + margin-top for the dialog body. */
          offsetSm={isTableside ? offsetSmIfTableside : offset}
        />
      </div>

      {topLevelOptionGroupsToShow.length === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} />
          <Grid item xs={12}>
            <Alert severity="info">
              No results for “{searchText}”
              <br />
              Please check your spelling and try another search, or browse for more options
            </Alert>
          </Grid>
        </Grid>
      )}
      {_.map(topLevelOptionGroupsToShow, (ticketOptionGroup) => {
        const ticketOptionGroupIsMatched =
          topLevelOptionGroupsWithFilter.includes(ticketOptionGroup);

        const ticketOptionsWithFilter = _.filter(
          ticketOptionGroupIsMatched ? ticketOption : topLevelOptionsWithFilter,
          (ticketOption) => ticketOption.ticketOptionGroupId === ticketOptionGroup.id
        );
        if (ticketOptionsWithFilter.length < 1) {
          return null;
        }

        return (
          <div key={ticketOptionGroup?.id ?? undefined} css={menuGroupContainerStyles}>
            <ScrollListNavigationPanel
              value={ticketOptionGroup?.id ?? undefined}
              inView={{
                threshold: 0.05,
                rootMargin,
                onChange: (isInView, value) =>
                  value
                    ? setOptionGroupsInView((prevInView) => ({
                        ...prevInView,
                        [value]: isInView,
                      }))
                    : undefined,
              }}
              idGenerator={(optionGroupId: number) => `menuOptionGroup-${optionGroupId}`}
            >
              <MenuGroup
                key={ticketOptionGroup.id as number}
                optionsWithModifiers={optionsWithModifiers}
                modifierOptionGroups={modifierOptionGroups}
                ticketOptions={ticketOptionsWithFilter}
                ticketOptionGroup={ticketOptionGroup}
                ticketOptionPrices={optionPrices}
                ticketType={ticketType}
                patron={patron}
              />
            </ScrollListNavigationPanel>
          </div>
        );
      })}
    </>
  );
};

export default Menu;
